import {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type FC,
  type ReactNode,
} from "react";
import { CheckCircleFilled, PlusOutlined } from "@ant-design/icons";
import { message, notification } from "antd";
import { useNavigate, useSearchParams } from "react-router-dom";

import AppContext from "../../../../context/AppContext";
import {
  GAME_PASS_DROPDOWN_LABELS,
  SIDER_TABS,
} from "../../../../utils/Constants";
import { NAVIGATION_ROUTES } from "../../../../utils/NavigationUtils";
import { AppStrings } from "../../../../utils/constants/AppStrings";
import { getApiErrorMessage } from "../../../../utils/ErrorUtils";
import type { SeasonData } from "../../../../types/gamePass";
import { Button, Modal, useTablistKeyboardNavigation } from "../../../shared";
import { usePageTitle } from "../../../CommonComponents/Layout/PageTitleContext";
import { useGetCampaignsListByStatuses } from "../../../../queries/game-pass/useGetCampaignsList";
import { useCloneCampaign } from "../../../../queries/game-pass/useCloneCampaign";
import { useDeleteCampaign } from "../../../../queries/game-pass/useDeleteCampaign";
import {
  campaignFilterIdToApiStatuses,
  partitionCampaignsByStatus,
} from "../../../../queries/game-pass/campaignsMappers";
import SeasonCard, {
  type SeasonCardProps,
} from "../../SeasonCard/SeasonCard";

import {
  DEFAULT_MONTHLY_FILTER,
  MONTHLY_FILTER_ID,
  MONTHLY_FILTER_QUERY_PARAM,
  MONTHLY_FILTER_TABS,
  parseMonthlyFilterId,
  type MonthlyFilterId,
} from "./monthlyConfigurationData";

import styles from "./MonthlyConfiguration.module.css";

type AppContextValue = {
  setActiveSidebarTab: (
    tabName: string,
    dropdownOption?: string | false,
  ) => void;
};

/** Inner layout per lifecycle section per panel:
 *   - "stack" → Timeline's narrow half-column; a single card / vertical list.
 *   - "grid"  → the "All" / single-status panels; cards lay out 2-per-row. */
type SectionLayout = "stack" | "grid";

/** Stable key for the duplicate-campaign success toast so re-clicking the
 *  icon replaces the existing toast (instead of stacking duplicates) and the
 *  inline "Drafts" link can dismiss it by key. Mirrors `SeasonsList`. */
const DUPLICATE_TOAST_KEY = "campaign-duplicate-toast";

/** Optional footer duplicate wiring for a card. Kept as an object so the
 *  builder's positional signature stays stable as affordances are added. */
type MonthlyCardDuplicate = {
  onDuplicate: () => void;
  duplicatePending?: boolean;
};

/** Per-section overrides so tests (and any future picker) can drive the
 *  populated / empty branches without hitting the API. `undefined` falls back
 *  to the fetched partition; `[]` forces the empty state. */
export type MonthlyConfigurationProps = {
  activeChallenges?: ReadonlyArray<SeasonData>;
  nextChallenges?: ReadonlyArray<SeasonData>;
  draftChallenges?: ReadonlyArray<SeasonData>;
  completedChallenges?: ReadonlyArray<SeasonData>;
  canceledChallenges?: ReadonlyArray<SeasonData>;
};

/**
 * Map a presentational `SeasonData` row into `SeasonCard` props.
 *
 * Every card is navigable — the title chevron (`onOpen`) opens the Monthly
 * Challenge Details view for all lifecycle states (Active, Scheduled, Draft,
 * Completed, Cancelled). The trailing only varies the extra bottom-row
 * affordance: editable statuses (Draft / Scheduled, `trailing === "edit"`)
 * expose a pencil; Active (`trailing === "locked"`) keeps the static Locked
 * badge; Completed / Cancelled add no trailing (their status pill already reads
 * Completed / Canceled) but stay clickable through the title chevron.
 *
 * `onDeleteDraft` adds a danger "Delete Monthly Challenge" secondary action —
 * only for draft cards (`status.tone === "draft"`), mirroring the Seasons
 * listing. A monthly challenge is only deletable while draft; the config
 * freezes at activation, so scheduled / active / completed / cancelled cards
 * never expose it.
 */
function buildMonthlyCardProps(
  challenge: SeasonData,
  testIdPrefix: string,
  onOpen?: () => void,
  onDeleteDraft?: () => void,
  duplicate?: MonthlyCardDuplicate,
): SeasonCardProps {
  const isEditable = challenge.trailing === "edit";
  const isLocked = challenge.trailing === "locked";
  const showDelete =
    challenge.status?.tone === "draft" && Boolean(onDeleteDraft);
  return {
    name: challenge.name,
    dateRange: challenge.dateRange,
    countdown: challenge.countdown,
    metrics: challenge.metrics,
    status: challenge.status,
    ...(onOpen ? { onOpen, openAriaLabel: challenge.name } : {}),
    trailing: isLocked
      ? { kind: "locked", label: AppStrings.SEASON_CARD_LOCKED }
      : isEditable && onOpen
        ? {
            kind: "edit",
            ariaLabel: `${AppStrings.MONTHLY_CHALLENGE_EDIT_ARIA} ${challenge.name}`,
            onClick: onOpen,
          }
        : undefined,
    ...(showDelete
      ? {
          secondaryAction: {
            label: AppStrings.MONTHLY_CHALLENGE_DELETE,
            ariaLabel: `${AppStrings.MONTHLY_CHALLENGE_DELETE_ARIA} ${challenge.name}`,
            onClick: onDeleteDraft,
            tone: "danger",
          },
        }
      : {}),
    // The duplicate icon shows on every lifecycle state (mirroring Seasons):
    // active, scheduled, draft, completed, and cancelled cards can all be
    // cloned into a fresh draft.
    ...(duplicate
      ? {
          onDuplicate: duplicate.onDuplicate,
          duplicateAriaLabel: `${AppStrings.GAME_PASS_MONTHLY_CHALLENGE_CARD_DUPLICATE} ${challenge.name}`,
          duplicatePending: duplicate.duplicatePending,
        }
      : {}),
    "data-testid": `${testIdPrefix}-${challenge.id}`,
  };
}

const MonthlyConfiguration: FC<MonthlyConfigurationProps> = ({
  activeChallenges: activeChallengesProp,
  nextChallenges: nextChallengesProp,
  draftChallenges: draftChallengesProp,
  completedChallenges: completedChallengesProp,
  canceledChallenges: canceledChallengesProp,
}) => {
  usePageTitle(AppStrings.MONTHLY_CONFIGURATION_PAGE_TITLE);

  const navigate = useNavigate();
  const { setActiveSidebarTab } = useContext(AppContext) as AppContextValue;

  // Active filter is driven by the `?filter=<id>` URL search param (not local
  // state) so a refresh / back-navigation preserves the operator's tab choice.
  const [searchParams, setSearchParams] = useSearchParams();
  const activeFilter =
    parseMonthlyFilterId(searchParams.get(MONTHLY_FILTER_QUERY_PARAM)) ??
    DEFAULT_MONTHLY_FILTER;

  // Per-tab API call — mirrors `SeasonsList`. The page asks
  // `campaignFilterIdToApiStatuses` which BE statuses populate the current
  // tab and hands that set to `useGetCampaignsListByStatuses`, which fans out
  // one parallel `?status=<X>` query per entry. Timeline fires active +
  // scheduled in parallel; the single-status tabs fire one query; "All"
  // fetches unfiltered and the partition slots records client-side. React
  // Query's key includes the status so switching tabs hits the cache or
  // refetches only the missing slice.
  const statusesFilter = campaignFilterIdToApiStatuses(activeFilter);
  const campaignsQuery = useGetCampaignsListByStatuses(statusesFilter);

  const partitionedCampaigns = useMemo(
    () => partitionCampaignsByStatus(campaignsQuery.data),
    [campaignsQuery.data],
  );

  // An explicit prop wins over the fetched partition. All overrides use `??`
  // since the sections are arrays — `[]` (a valid empty-state override) is
  // preserved because it's never null/undefined.
  const activeChallenges = activeChallengesProp ?? partitionedCampaigns.active;
  const nextChallenges = nextChallengesProp ?? partitionedCampaigns.next;
  const draftChallenges = draftChallengesProp ?? partitionedCampaigns.drafts;
  const completedChallenges =
    completedChallengesProp ?? partitionedCampaigns.completed;
  const canceledChallenges =
    canceledChallengesProp ?? partitionedCampaigns.canceled;

  // The loading placeholder replaces the panel only in pure-API mode (no
  // overrides). Any override implies the caller drives the data (tests,
  // pickers). Errors deliberately get no placeholder — the partition falls
  // back to empty arrays and each section renders its own empty state, while
  // the axios interceptor still surfaces the BE error toast.
  const hasAnyOverride =
    activeChallengesProp !== undefined ||
    nextChallengesProp !== undefined ||
    draftChallengesProp !== undefined ||
    completedChallengesProp !== undefined ||
    canceledChallengesProp !== undefined;
  const showLoadingPlaceholder = !hasAnyOverride && campaignsQuery.isLoading;

  const handleSelectFilter = useCallback(
    (tabId: MonthlyFilterId) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (tabId === DEFAULT_MONTHLY_FILTER) {
            next.delete(MONTHLY_FILTER_QUERY_PARAM);
          } else {
            next.set(MONTHLY_FILTER_QUERY_PARAM, tabId);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const { tabRefs: filterTabRefs, handleKeyDown: handleFilterKeyDown } =
    useTablistKeyboardNavigation<MonthlyFilterId>({
      items: MONTHLY_FILTER_TABS,
      value: activeFilter,
      onChange: handleSelectFilter,
    });

  useEffect(() => {
    setActiveSidebarTab(SIDER_TABS?.GAME_PASS, GAME_PASS_DROPDOWN_LABELS.MONTHLY);
  }, [setActiveSidebarTab]);

  const goToCreateMonthlyChallenge = useCallback(() => {
    navigate(NAVIGATION_ROUTES.GAME_PASS_MONTHLY_NEW);
  }, [navigate]);

  const openMonthlyDetails = useCallback(
    (challenge: SeasonData) => {
      navigate(
        NAVIGATION_ROUTES.GAME_PASS_MONTHLY_DETAILS.replace(
          ":challengeId",
          challenge.id,
        ),
      );
    },
    [navigate],
  );

  // Draft-only delete, mirroring the Seasons listing. The confirm modal is
  // shared across every draft card; `pendingDelete` holds the card awaiting
  // confirmation. On success `useDeleteCampaign` drops the row from the
  // campaigns-list cache, so the card disappears without leaving the page.
  const deleteCampaignMutation = useDeleteCampaign();
  const [pendingDelete, setPendingDelete] = useState<SeasonData | null>(null);

  const handleOpenDeleteDraft = useCallback((challenge: SeasonData) => {
    setPendingDelete(challenge);
  }, []);

  const handleCloseDeleteModal = useCallback(() => {
    if (deleteCampaignMutation.isPending) return;
    setPendingDelete(null);
  }, [deleteCampaignMutation.isPending]);

  const handleConfirmDelete = useCallback(async () => {
    if (!pendingDelete) return;
    try {
      await deleteCampaignMutation.mutateAsync(pendingDelete.id);
      setPendingDelete(null);
    } catch (error) {
      message.error(
        getApiErrorMessage(error, AppStrings.MONTHLY_CHALLENGE_DELETE_ERROR),
      );
    }
  }, [deleteCampaignMutation, pendingDelete]);

  // Duplicating a monthly challenge clones it (challenges + tasks) into a new
  // draft via the clone endpoint. On success we confirm with a bottom-centred
  // toast whose "Drafts" link jumps the listing to the Drafts tab so the admin
  // can find the copy. We use antd `notification` (not `message`) so the toast
  // can anchor to `placement: "bottom"`. Failures fall back to the standard top
  // `message.error` surface (with the BE message when present). Mirrors
  // `SeasonsList.handleDuplicateSeason`.
  const cloneCampaignMutation = useCloneCampaign();

  const handleDuplicateCampaign = useCallback(
    async (challenge: SeasonData) => {
      try {
        const cloned = await cloneCampaignMutation.mutateAsync(challenge.id);
        const duplicateName =
          cloned.name?.trim() ||
          `${challenge.name}${AppStrings.GAME_PASS_MONTHLY_CHALLENGE_DUPLICATE_NAME_SUFFIX}`;
        notification.success({
          key: DUPLICATE_TOAST_KEY,
          placement: "bottom",
          duration: 4,
          className: styles.duplicateToast,
          closeIcon: null,
          icon: <CheckCircleFilled className={styles.duplicateToastIcon} />,
          message: (
            <span className={styles.duplicateToastContent}>
              {`"${duplicateName}" ${AppStrings.GAME_PASS_MONTHLY_CHALLENGE_DUPLICATE_ADDED_TO} `}
              <button
                type="button"
                className={styles.duplicateToastLink}
                onClick={() => {
                  notification.destroy(DUPLICATE_TOAST_KEY);
                  handleSelectFilter(MONTHLY_FILTER_ID.DRAFTS);
                }}
                data-testid="monthly-configuration-duplicate-toast-drafts-link"
              >
                {AppStrings.SEASONS_FILTER_DRAFTS}
              </button>
            </span>
          ),
        });
      } catch (error) {
        message.error(
          getApiErrorMessage(
            error,
            AppStrings.GAME_PASS_MONTHLY_CHALLENGE_DUPLICATE_ERROR,
          ),
        );
      }
    },
    [cloneCampaignMutation, handleSelectFilter],
  );

  const renderEmptyCreateCard = (
    layout: SectionLayout,
    title: string,
    testId: string,
  ): ReactNode => {
    const card = (
      <button
        type="button"
        className={styles.emptyCard}
        onClick={goToCreateMonthlyChallenge}
        data-testid={testId}
      >
        <p className={styles.emptyTitle}>{title}</p>
        <PlusOutlined className={styles.emptyIcon} aria-hidden="true" />
        <p className={styles.emptyCta}>
          {AppStrings.MONTHLY_CONFIGURATION_CREATE_A_CHALLENGE}
        </p>
      </button>
    );

    // In grid layouts (All / single-status tabs) the create card sits in one
    // grid cell so it reads at half width like the populated cards, instead of
    // stretching the full section. In stack layouts (Timeline) it already fills
    // its narrow half-column, so no grid wrapper is needed.
    return layout === "grid" ? (
      <div className={styles.sectionGrid} data-testid={`${testId}-grid`}>
        {card}
      </div>
    ) : (
      card
    );
  };

  const renderCardList = (
    challenges: ReadonlyArray<SeasonData>,
    layout: SectionLayout,
    testIdPrefix: string,
    onDeleteDraft?: (challenge: SeasonData) => void,
  ): ReactNode => (
    <ul
      className={layout === "grid" ? styles.sectionGrid : styles.cardList}
      data-testid={`${testIdPrefix}-list`}
    >
      {challenges.map((challenge) => (
        <li key={challenge.id} className={styles.cardItem}>
          <SeasonCard
            {...buildMonthlyCardProps(
              challenge,
              testIdPrefix,
              () => openMonthlyDetails(challenge),
              onDeleteDraft ? () => onDeleteDraft(challenge) : undefined,
              {
                onDuplicate: () => handleDuplicateCampaign(challenge),
                duplicatePending: cloneCampaignMutation.isPending,
              },
            )}
          />
        </li>
      ))}
    </ul>
  );

  // Unlike Seasons (one active at a time), the campaign backend allows several
  // concurrent active monthly challenges, so this renders the full set as a
  // list rather than a single card — none are silently hidden.
  const renderActiveSection = (layout: SectionLayout): ReactNode => (
    <section
      key="active"
      className={styles.column}
      aria-labelledby="monthly-configuration-active-title"
      data-testid="monthly-configuration-section-active"
    >
      <h2 id="monthly-configuration-active-title" className={styles.columnTitle}>
        <span className={styles.activeDot} aria-hidden="true" />
        {AppStrings.MONTHLY_CONFIGURATION_ACTIVE_MONTH}
      </h2>
      {activeChallenges.length > 0
        ? renderCardList(activeChallenges, layout, "monthly-configuration-active")
        : renderEmptyCreateCard(
            layout,
            AppStrings.MONTHLY_CONFIGURATION_ACTIVE_EMPTY_TITLE,
            "monthly-configuration-active-empty",
          )}
    </section>
  );

  const renderNextSection = (layout: SectionLayout): ReactNode => (
    <section
      key="next"
      className={styles.column}
      aria-labelledby="monthly-configuration-next-title"
      data-testid="monthly-configuration-section-next"
    >
      <h2 id="monthly-configuration-next-title" className={styles.columnTitle}>
        {AppStrings.SEASONS_FILTER_SCHEDULED}
      </h2>
      {nextChallenges.length > 0
        ? renderCardList(nextChallenges, layout, "monthly-configuration-next")
        : renderEmptyCreateCard(
            layout,
            AppStrings.MONTHLY_CONFIGURATION_NEXT_EMPTY_TITLE,
            "monthly-configuration-next-empty",
          )}
    </section>
  );

  const renderDraftsSection = (layout: SectionLayout): ReactNode => (
    <section
      key="drafts"
      className={styles.column}
      aria-labelledby="monthly-configuration-drafts-title"
      data-testid="monthly-configuration-section-drafts"
    >
      <h2 id="monthly-configuration-drafts-title" className={styles.columnTitle}>
        {AppStrings.SEASONS_FILTER_DRAFTS}
      </h2>
      {draftChallenges.length > 0 ? (
        renderCardList(
          draftChallenges,
          layout,
          "monthly-configuration-drafts",
          handleOpenDeleteDraft,
        )
      ) : (
        <p
          className={styles.emptyText}
          data-testid="monthly-configuration-drafts-empty"
        >
          {AppStrings.MONTHLY_CONFIGURATION_DRAFTS_EMPTY_TITLE}
        </p>
      )}
    </section>
  );

  const renderCompletedSection = (layout: SectionLayout): ReactNode => (
    <section
      key="completed"
      className={styles.column}
      aria-labelledby="monthly-configuration-completed-title"
      data-testid="monthly-configuration-section-completed"
    >
      <h2
        id="monthly-configuration-completed-title"
        className={styles.columnTitle}
      >
        {AppStrings.SEASONS_FILTER_COMPLETED}
      </h2>
      {completedChallenges.length > 0 ? (
        renderCardList(
          completedChallenges,
          layout,
          "monthly-configuration-completed",
        )
      ) : (
        <p
          className={styles.emptyText}
          data-testid="monthly-configuration-completed-empty"
        >
          {AppStrings.MONTHLY_CONFIGURATION_COMPLETED_EMPTY_TITLE}
        </p>
      )}
    </section>
  );

  const renderCanceledSection = (layout: SectionLayout): ReactNode => (
    <section
      key="canceled"
      className={styles.column}
      aria-labelledby="monthly-configuration-canceled-title"
      data-testid="monthly-configuration-section-canceled"
    >
      <h2
        id="monthly-configuration-canceled-title"
        className={styles.columnTitle}
      >
        {AppStrings.SEASONS_FILTER_CANCELED}
      </h2>
      {canceledChallenges.length > 0 ? (
        renderCardList(
          canceledChallenges,
          layout,
          "monthly-configuration-canceled",
        )
      ) : (
        <p
          className={styles.emptyText}
          data-testid="monthly-configuration-canceled-empty"
        >
          {AppStrings.MONTHLY_CONFIGURATION_CANCELED_EMPTY_TITLE}
        </p>
      )}
    </section>
  );

  let panelClassName: string;
  let panelContent: ReactNode;
  // The loading placeholder short-circuits the per-filter switch so admins
  // don't see a flash of empty-state cards while the first fetch is in flight.
  // On error we fall through to the normal render — each section owns its own
  // empty state, which doubles as the "nothing to show" UI for a failed fetch.
  if (showLoadingPlaceholder) {
    panelClassName = styles.stack;
    panelContent = (
      <p
        className={styles.panelPlaceholder}
        data-testid="monthly-configuration-panel-loading"
      >
        {AppStrings.MONTHLY_CONFIGURATION_LOADING}
      </p>
    );
  } else {
    switch (activeFilter) {
      case MONTHLY_FILTER_ID.TIMELINE:
        panelClassName = styles.timeline;
        panelContent = (
          <>
            {renderActiveSection("stack")}
            {renderNextSection("stack")}
          </>
        );
        break;
      case MONTHLY_FILTER_ID.ALL:
        panelClassName = styles.stack;
        panelContent = (
          <>
            {renderActiveSection("grid")}
            {renderNextSection("grid")}
            {renderDraftsSection("grid")}
            {renderCompletedSection("grid")}
            {renderCanceledSection("grid")}
          </>
        );
        break;
      case MONTHLY_FILTER_ID.SCHEDULED:
        panelClassName = styles.stack;
        panelContent = renderNextSection("grid");
        break;
      case MONTHLY_FILTER_ID.DRAFTS:
        panelClassName = styles.stack;
        panelContent = renderDraftsSection("grid");
        break;
      case MONTHLY_FILTER_ID.COMPLETED:
        panelClassName = styles.stack;
        panelContent = renderCompletedSection("grid");
        break;
      case MONTHLY_FILTER_ID.CANCELED:
        panelClassName = styles.stack;
        panelContent = renderCanceledSection("grid");
        break;
      default:
        panelClassName = styles.stack;
        panelContent = (
          <p
            className={styles.panelPlaceholder}
            data-testid="monthly-configuration-panel-placeholder"
          >
            {AppStrings.MONTHLY_CONFIGURATION_NO_DATA}
          </p>
        );
    }
  }

  return (
    <>
      <div className={styles.page} data-testid="monthly-configuration">
        <header className={styles.header}>
          <div className={styles.headerActions}>
            <Button
              intent="primary"
              onClick={goToCreateMonthlyChallenge}
              data-testid="monthly-configuration-create"
            >
              {AppStrings.MONTHLY_CONFIGURATION_CREATE_CTA}
            </Button>
          </div>
        </header>

        <div
          className={styles.tabs}
          role="tablist"
          aria-label={AppStrings.MONTHLY_CONFIGURATION_FILTER_BAR_ARIA}
          data-testid="monthly-configuration-filter-tabs"
        >
          {MONTHLY_FILTER_TABS.map((tab, index) => {
            const isActive = tab.id === activeFilter;
            return (
              <button
                key={tab.id}
                ref={(node) => {
                  filterTabRefs.current[index] = node;
                }}
                id={`monthly-configuration-filter-${tab.id}`}
                type="button"
                role="tab"
                aria-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={`${styles.tab} ${isActive ? styles.tabActive : ""}`}
                onClick={() => handleSelectFilter(tab.id)}
                onKeyDown={(event) => handleFilterKeyDown(event, index)}
                data-testid={`monthly-configuration-filter-${tab.id}`}
              >
                {tab.label}
              </button>
            );
          })}
        </div>

        <div
          className={panelClassName}
          role="tabpanel"
          aria-labelledby={`monthly-configuration-filter-${activeFilter}`}
          data-testid={`monthly-configuration-panel-${activeFilter}`}
        >
          {panelContent}
        </div>

        <Modal
          open={Boolean(pendingDelete)}
          onClose={handleCloseDeleteModal}
          intent="destructive"
          size="sm"
          destroyOnHidden
          title={AppStrings.MONTHLY_CHALLENGE_DELETE}
          footer={
            <div className={styles.deleteModalFooter}>
              <Button
                intent="neutral"
                variant="outline"
                onClick={handleCloseDeleteModal}
                disabled={deleteCampaignMutation.isPending}
                data-testid="monthly-configuration-delete-go-back"
              >
                {AppStrings.MONTHLY_CHALLENGE_DELETE_GO_BACK}
              </Button>
              <Button
                intent="danger"
                loading={deleteCampaignMutation.isPending}
                onClick={handleConfirmDelete}
                data-testid="monthly-configuration-delete-confirm"
              >
                {AppStrings.MONTHLY_CHALLENGE_DELETE}
              </Button>
            </div>
          }
        >
          <p
            className={styles.deleteModalBody}
            data-testid="monthly-configuration-delete-body"
          >
            {AppStrings.MONTHLY_CHALLENGE_DELETE_CONFIRM_BODY}
          </p>
        </Modal>
      </div>
    </>
  );
};

export default MonthlyConfiguration;
